import React, { useCallback, useEffect, useMemo, useState } from "react"
import PropTypes from "prop-types"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { Button, Flex } from "antd"
import { routes } from "router"
import { getApiErrorMessage } from "helpers/embedded"
import { addToWidgetCart, changeWidgetCartItemChoice, hasCartError, resetBookingSelections, updateBookingSelections } from "store/experiences"
import { formatDurationFields } from "utils/experience"
import { getMultiDaySpan } from "utils/experienceAvailability"
import { getSlotStatus, getTicketedChoice, trimTicketCounts } from "utils/ticketedBooking"
import useTicketedAvailability from "hooks/useTicketedAvailability"
import { getBookableTicketTypes } from "../../helpers"
import { buildTicketedPanelSummary, getTicketDayPrices, getTicketDiscountsByDate } from "../pricing"
import { describeAvailabilityRefusal } from "../../CartHold/holdIssue"
import ReservationExpiredNotice from "../../CartHold/ReservationExpiredNotice"
import useCartHold from "../../CartHold/useCartHold"
import useCartItemBeingChanged from "./useCartItemBeingChanged"
import { CART_ERRORS } from "../../constants"
import TotalGuestsSection from "./TotalGuestsSection"
import DateSection from "./DateSection"
import StartTimeSection from "./StartTimeSection"
import TicketsSection from "./TicketsSection"
import PriceSummary from "./PriceSummary"
import PanelFrame from "./PanelFrame"
import styles from "./BookingPanel.module.css"

const NO_ERRORS = { guests: null, date: null, time: null, tickets: null }
const EMPTY_TICKET_COUNTS = {}

// "Real-Time Availability" panel for a ticketed experience: total guests -> date -> start time ->
// tickets, with the live price. Bookable days and start times, with places left, come from the API
// (useTicketedAvailability): guests and each ticket type stop at the places left, and a choice that
// no longer fits shows "Availability has changed" and can't be added. The choices are a draft in the
// shared bookingSelections store, and Add to Cart adds a copy to the cart, as in the private panel.
// Opened from a cart item's "Choose another time" it starts from that item's choices and Save
// Changes changes it.
const TicketedBookingPanel = ({ experience, isSheetOpen, onSheetClose }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { uuid, id } = useParams()
  const { bookingSelections } = useSelector((state) => state.experiences.experienceBookingData)
  const changing = useCartItemBeingChanged()
  const [errors, setErrors] = useState(NO_ERRORS)
  const [calendarMonth, setCalendarMonth] = useState(null)
  const [isAdding, setIsAdding] = useState(false)
  // Add to Cart refused: the API's availability reason (shown over the tickets until the choice
  // changes), or any other error (shown by the button).
  const [refusal, setRefusal] = useState({ warning: null, error: null })
  const [availabilityRefreshKey, setAvailabilityRefreshKey] = useState(0)

  const { date, scheduledSlot, totalGuests = 0 } = bookingSelections
  const ticketCounts = bookingSelections.ticketCounts || EMPTY_TICKET_COUNTS
  const ticketTypes = useMemo(() => getBookableTicketTypes(experience), [experience])
  const minGuests = Math.max(1, Number(experience?.min_tickets) || 0)
  const isMultiDay = experience?.duration_type === "multi_day"
  const spanDays = isMultiDay ? getMultiDaySpan(experience) : 1
  const duration = isMultiDay ? null : formatDurationFields(experience, t)

  const { bookableDates, daySlots, isSlotsLoading } = useTicketedAvailability({
    experienceId: id,
    experience,
    date,
    month: calendarMonth,
    refreshKey: availabilityRefreshKey
  })
  const discountByDate = useMemo(() => getTicketDiscountsByDate(experience, bookableDates), [experience, bookableDates])
  const getDayPrices = useCallback((day) => getTicketDayPrices(experience, day), [experience])
  // A kept draft's start time counts only while it is still on offer (see getTicketedChoice).
  const { selectedSlot, ticketsChosen, remaining, maxGuests, typeLimits, availabilityWarning } = getTicketedChoice({
    t,
    experience,
    ticketTypes,
    ticketCounts,
    daySlots,
    scheduledSlot,
    date,
    totalGuests,
    isSlotsLoading
  })
  const slotStatus = useMemo(() => getSlotStatus(experience, daySlots), [experience, daySlots])

  // A new choice starts without the last refusal.
  useEffect(() => {
    setRefusal({ warning: null, error: null })
  }, [date, scheduledSlot?.id, totalGuests, ticketCounts])

  // Coming back to the same experience keeps the draft; another experience starts a new one (the
  // cart can hold this experience more than once, so it is not a draft source). Changing a cart item
  // starts from its choices.
  useEffect(() => {
    if (!id || (!changing.selection && bookingSelections.experienceId === id)) return
    dispatch(resetBookingSelections())
    dispatch(updateBookingSelections({ ...changing.selection, experienceId: id }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, changing.cartId, dispatch])

  const selectSlot = (slot) => {
    dispatch(updateBookingSelections({ timeSlot: slot.label, scheduledSlot: { id: slot.id, startsAt: slot.startsAt } }))
    setErrors((prev) => ({ ...prev, time: null }))
  }

  // A single start time needs no choice.
  useEffect(() => {
    if (daySlots.length === 1 && !selectedSlot && !scheduledSlot?.id && !daySlots[0].isSoldOut) selectSlot(daySlots[0])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [daySlots, selectedSlot])

  const handleGuestsChange = (next) => {
    const nextTotal = Math.max(0, next)
    dispatch(updateBookingSelections({ totalGuests: nextTotal, ticketCounts: trimTicketCounts(ticketTypes, ticketCounts, nextTotal) }))
    setErrors((prev) => ({ ...prev, guests: null, tickets: null }))
  }

  const handleDateChange = (nextDate) => {
    dispatch(updateBookingSelections({ date: nextDate, timeSlot: null, scheduledSlot: null }))
    setErrors((prev) => ({ ...prev, date: null, time: null }))
  }

  const handleTimeChange = (label) => {
    const slot = daySlots.find((item) => item.label === label)
    if (slot && !slot.isSoldOut) selectSlot(slot)
  }

  const handleTicketsChange = (nextCounts) => {
    dispatch(updateBookingSelections({ ticketCounts: nextCounts }))
    setErrors((prev) => ({ ...prev, tickets: null }))
  }

  const summary = useMemo(
    () => (ticketsChosen > 0 ? buildTicketedPanelSummary({ experience, date, ticketCounts }) : null),
    [experience, date, ticketCounts, ticketsChosen]
  )

  const validate = () => {
    const nextErrors = {
      guests: totalGuests >= minGuests ? null : t("booking_widget.experience.errors.guests", { count: minGuests }),
      date: date ? null : t("booking_widget.experience.errors.date"),
      time: selectedSlot ? null : t("booking_widget.experience.errors.time"),
      // "for 5 guests" before any ticket is chosen, "for 2 more guests" once some are.
      tickets:
        totalGuests > 0 && remaining > 0
          ? t(`booking_widget.experience.errors.${ticketsChosen > 0 ? "tickets" : "tickets_none"}`, { count: remaining })
          : null
    }
    setErrors(nextErrors)
    return Object.values(nextErrors).every((error) => !error)
  }

  // While the cart's reservation is over nothing more is added until "Check availability & continue"
  // reserves it again (the API refuses, hold_expired).
  const { isReservationOver } = useCartHold()

  // The places are held first (addToWidgetCart); the same date and start time changes the item
  // already in the cart. Refused when the places are gone, e.g. the guest's own first item on this
  // start time already takes them.
  const handleAddToCart = async () => {
    if (isAdding || isReservationOver || !validate() || availabilityWarning) return
    setIsAdding(true)
    try {
      const selection = { ...bookingSelections, ...(summary && { amount: summary.total }) }
      await dispatch(changing.cartId ? changeWidgetCartItemChoice(changing.cartId, selection) : addToWidgetCart(selection))
      navigate(routes.widgetCart({ uuid }))
    } catch (error) {
      setIsAdding(false)
      // The cart was read again and now shows the expired notice
      if (hasCartError(error, CART_ERRORS.holdExpired)) return
      const warning = describeAvailabilityRefusal({ message: error?.data?.message, experience, t })
      setRefusal({ warning, error: warning ? null : getApiErrorMessage(error) || t("booking_widget.checkout.errors.generic") })
      if (warning) setAvailabilityRefreshKey((key) => key + 1)
    }
  }

  const actions = (
    <>
      {refusal.error && (
        <span className={styles.errorText} role="alert">
          {refusal.error}
        </span>
      )}
      {isReservationOver && <ReservationExpiredNotice />}
      {changing.cartId && <span className={styles.paymentNote}>{t("booking_widget.experience.changing_item")}</span>}
      <Button type="primary" shape="round" size="large" block loading={isAdding} disabled={isReservationOver} onClick={handleAddToCart}>
        {t(changing.cartId ? "booking_widget.experience.save_changes" : "booking_widget.experience.add_to_cart")}
      </Button>
      <span className={styles.paymentNote}>{t("booking_widget.experience.payment_note")}</span>
    </>
  )

  return (
    <PanelFrame isSheetOpen={isSheetOpen} onSheetClose={onSheetClose} actions={actions}>
      {isMultiDay && (
        <Flex vertical gap={2} className={styles.multiDayBanner}>
          <span className={styles.multiDayBannerTitle}>{t("booking_widget.experience.multi_day_title", { count: spanDays })}</span>
          <span>{t("booking_widget.experience.multi_day_hint")}</span>
        </Flex>
      )}
      {duration && (
        <span className={`${styles.multiDayBanner} ${styles.durationBanner}`}>{t("booking_widget.experience.duration_banner", { duration })}</span>
      )}
      <TotalGuestsSection value={totalGuests} maxGuests={maxGuests} onChange={handleGuestsChange} error={errors.guests} />
      {/* Keyed by the draft's experience: the calendar reopens on the right month once this
          experience's draft replaces the previous one. */}
      <DateSection
        key={bookingSelections.experienceId}
        experience={experience}
        value={date}
        onChange={handleDateChange}
        error={errors.date}
        spanDays={spanDays}
        bookableDates={bookableDates}
        discountByDate={discountByDate}
        getDayPrices={getDayPrices}
        onMonthChange={setCalendarMonth}
      />
      <StartTimeSection
        slots={daySlots.map((slot) => slot.label)}
        slotStatus={slotStatus}
        selectedSlot={selectedSlot?.label || null}
        isLoading={isSlotsLoading}
        hasDate={!!date}
        onSelect={handleTimeChange}
        error={errors.time}
      />
      <TicketsSection
        experience={experience}
        ticketTypes={ticketTypes}
        date={date}
        totalGuests={totalGuests}
        ticketCounts={ticketCounts}
        typeLimits={typeLimits}
        warning={availabilityWarning || refusal.warning}
        onChange={handleTicketsChange}
        error={errors.tickets}
      />
      {summary && <PriceSummary summary={summary} />}
    </PanelFrame>
  )
}

TicketedBookingPanel.propTypes = {
  experience: PropTypes.object.isRequired,
  isSheetOpen: PropTypes.bool,
  onSheetClose: PropTypes.func
}

TicketedBookingPanel.defaultProps = {
  isSheetOpen: false,
  onSheetClose: () => {}
}

export default TicketedBookingPanel
